import React, { useEffect, useRef, useState } from "react";

// Tables are made independently scrollable by App.css. Using the table itself
// also avoids nested Bootstrap wrappers swallowing the synchronized scroll.
const getScroller = (table) => table;

const TableScrollAssist = () => {
  const barRef = useRef(null);
  const activeScrollerRef = useRef(null);
  const syncingRef = useRef(false);
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    let frameId;

    const update = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const viewportHeight = window.innerHeight;
        const candidates = Array.from(document.querySelectorAll(".App table"))
          .map((table) => {
            const scroller = getScroller(table);
            const rect = table.getBoundingClientRect();
            const visibleHeight = Math.max(
              0,
              Math.min(rect.bottom, viewportHeight) - Math.max(rect.top, 0)
            );
            return { table, scroller, rect, visibleHeight };
          })
          .filter(({ scroller, visibleHeight }) =>
            visibleHeight > 0 && scroller.scrollWidth > scroller.clientWidth + 1
          )
          .sort((a, b) => b.visibleHeight - a.visibleHeight);

        const active = candidates[0]?.scroller || null;
        activeScrollerRef.current = active;

        if (!active) {
          setMetrics(null);
          return;
        }

        const width = Math.min(active.clientWidth, window.innerWidth - 24);
        const contentWidth = active.scrollWidth;
        setMetrics((previous) =>
          previous?.width === width && previous?.contentWidth === contentWidth
            ? previous
            : { width, contentWidth }
        );

        if (barRef.current) {
          syncingRef.current = true;
          barRef.current.scrollLeft = active.scrollLeft;
          syncingRef.current = false;
        }
      });
    };

    const syncFromTable = (event) => {
      if (event.target !== activeScrollerRef.current || !barRef.current) return;
      syncingRef.current = true;
      barRef.current.scrollLeft = event.target.scrollLeft;
      syncingRef.current = false;
    };

    const observer = new MutationObserver(update);
    observer.observe(document.querySelector(".App") || document.body, {
      childList: true,
      subtree: true,
    });

    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    document.addEventListener("scroll", syncFromTable, true);
    update();

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      document.removeEventListener("scroll", syncFromTable, true);
    };
  }, []);

  const handleScroll = (event) => {
    if (syncingRef.current || !activeScrollerRef.current) return;
    activeScrollerRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  if (!metrics) return null;

  return (
    <div
      className="table-scroll-assist"
      ref={barRef}
      onScroll={handleScroll}
      aria-label="Horizontal table scrollbar"
      role="region"
      style={{ width: metrics.width }}
    >
      <div style={{ width: metrics.contentWidth, height: 1 }} />
    </div>
  );
};

export default TableScrollAssist;
